import React from 'react';
import { Card } from '@components/common/card';
import { Button } from '@components/common/button';
import {
  Gauge,
  MapPin,
  User,
  Calendar,
  Droplets,
  Activity,
  Eye,
  Edit,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  TrendingUp,
  Building2
} from 'lucide-react';
import { Meter, MeterStatus } from '@/types/meter_types';

interface MeterCardProps {
  meter: Meter;
  on_view?: (meter: Meter) => void;
  on_edit?: (meter: Meter) => void;
  on_add_reading?: (meter: Meter) => void;
  show_actions?: boolean;
  compact?: boolean;
}

export const MeterCard: React.FC<MeterCardProps> = ({
  meter,
  on_view,
  on_edit,
  on_add_reading,
  show_actions = true,
  compact = false
}) => {
  // Helper to get last reading value
  const get_last_reading = (): number | null => {
    if ((meter as any).latest_reading?.current_reading) {
      return parseFloat((meter as any).latest_reading.current_reading);
    }
    return null;
  };

  // Helper to get consumption
  const get_consumption = (): number | null => {
    if ((meter as any).latest_reading?.consumption) {
      const consumption = parseFloat((meter as any).latest_reading.consumption);
      return Math.abs(consumption); // Use absolute value
    }
    return null;
  };

  const get_status_badge = (status: MeterStatus) => {
    const status_lower = status.toLowerCase();
    
    const styles: Record<string, string> = {
      active: 'bg-success bg-opacity-10 text-success',
      inactive: 'bg-gray-100 text-gray-600',
      faulty: 'bg-error bg-opacity-10 text-error',
      maintenance: 'bg-warning bg-opacity-10 text-warning',
      replaced: 'bg-gray-100 text-gray-600',
      removed: 'bg-gray-100 text-gray-600'
    };

    const icons: Record<string, React.ReactNode> = {
      active: <CheckCircle size={14} />,
      inactive: <XCircle size={14} />,
      faulty: <AlertTriangle size={14} />,
      maintenance: <Clock size={14} />,
      replaced: <XCircle size={14} />,
      removed: <XCircle size={14} />
    };

    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${styles[status_lower] || styles.inactive}`}>
        {icons[status_lower] || icons.inactive}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const needs_reading = () => {
    if (!meter.last_reading_date) return true;
    const days_since_reading = Math.floor(
      (Date.now() - new Date(meter.last_reading_date).getTime()) / (1000 * 60 * 60 * 24)
    );
    return days_since_reading > 30;
  };

  const days_since_last_reading = () => {
    if (!meter.last_reading_date) return null;
    return Math.floor(
      (Date.now() - new Date(meter.last_reading_date).getTime()) / (1000 * 60 * 60 * 24)
    );
  };

  const last_reading = get_last_reading();
  const consumption = get_consumption();
  const reading_needed = needs_reading();
  const days_since = days_since_last_reading();

  // Parse location from notes field
  const parse_location = () => {
    if (!meter.notes) return null;
    const parts = meter.notes.split(' | ');
    return {
      location: parts[0] || null,
      property: parts[1] || null,
      owner: parts[2] || null,
      unit: parts[3] || null
    };
  };

  const location_info = parse_location();

  // Handle card click - but prevent if clicking action buttons
  const handle_card_click = (e?: React.MouseEvent) => {
    // If no event or clicking on buttons/actions, don't trigger card click
    if (!e) return;
    
    const target = e.target as HTMLElement;
    if (target.closest('button')) {
      return;
    }
    on_view?.(meter);
  };

  if (compact) {
    return (
      <div
        className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-smooth cursor-pointer"
        onClick={handle_card_click}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 flex-1">
            <div className="w-12 h-12 rounded-lg bg-forest-green bg-opacity-10 text-forest-green flex items-center justify-center flex-shrink-0">
              <Gauge size={24} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-charcoal truncate">
                {meter.meter_number}
              </h3>
              <p className="text-sm text-gray-500 truncate">{meter.client_name}</p>
              {last_reading !== null && (
                <p className="text-xs text-gray-400 mt-1">
                  {last_reading.toLocaleString()} units
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {get_status_badge(meter.status)}
            {on_add_reading && (meter as any).status === 'ACTIVE' && (
              <Button
                variant="primary"
                size="sm"
                icon_left={<Activity size={14} />}
                onClick={(e) => {
                  e?.stopPropagation();
                  on_add_reading(meter);
                }}
              >
                Read
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <Card
      is_clickable={!!on_view}
      onClick={handle_card_click}
      className="h-full"
    >
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-forest-green to-dark-forest text-white flex items-center justify-center">
              <Gauge size={28} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-charcoal">
                {meter.meter_number}
              </h3>
              {get_status_badge(meter.status)}
            </div>
          </div>
        </div>

        {/* Client & Location Info */}
        <div className="space-y-2 pt-3 border-t border-gray-200">
          <div className="flex items-center gap-2 text-sm">
            <User size={16} className="text-gray-400 flex-shrink-0" />
            <span className="text-gray-700 truncate">{meter.client_name}</span>
          </div>

          {location_info?.property && (
            <div className="flex items-center gap-2 text-sm">
              <Building2 size={16} className="text-gray-400 flex-shrink-0" />
              <span className="text-gray-700 truncate">
                {location_info.property}
                {location_info.unit && ` - ${location_info.unit}`}
              </span>
            </div>
          )}

          {location_info?.location && (
            <div className="flex items-center gap-2 text-sm">
              <MapPin size={16} className="text-gray-400 flex-shrink-0" />
              <span className="text-gray-700 truncate">{location_info.location}</span>
            </div>
          )}

          {meter.installation_date && (
            <div className="flex items-center gap-2 text-sm">
              <Calendar size={16} className="text-gray-400 flex-shrink-0" />
              <span className="text-gray-500 text-xs">
                Installed: {new Date(meter.installation_date).toLocaleDateString()}
              </span>
            </div>
          )}
        </div>

        {/* Reading Information */}
        <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-200">
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-center gap-1 text-gray-500 mb-1">
              <Droplets size={14} />
            </div>
            <p className="text-xs text-gray-500">Last Reading</p>
            {last_reading !== null ? (
              <>
                <p className="text-lg font-semibold text-charcoal">
                  {last_reading.toLocaleString()}
                </p>
                <p className="text-xs text-gray-400">units</p>
              </>
            ) : (
              <p className="text-sm text-gray-400 italic">No readings</p>
            )}
          </div>

          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-center gap-1 text-gray-500 mb-1">
              <TrendingUp size={14} />
            </div>
            <p className="text-xs text-gray-500">Consumption</p>
            {consumption !== null && consumption > 0 ? (
              <>
                <p className="text-lg font-semibold text-forest-green">
                  {consumption.toLocaleString()}
                </p>
                <p className="text-xs text-gray-400">units</p>
              </>
            ) : (
              <p className="text-sm text-gray-400">-</p>
            )}
          </div>
        </div>

        {/* Last Reading Date */}
        {meter.last_reading_date && (
          <div className="pt-3 border-t border-gray-200">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Last recorded</span>
              <span className="font-medium text-charcoal">
                {days_since !== null ? `${days_since} days ago` : 'N/A'}
              </span>
            </div>
          </div>
        )}

        {/* Warning for Reading Due */}
        {reading_needed && (meter as any).status === 'ACTIVE' && (
          <div className="flex items-center gap-2 p-3 bg-warning bg-opacity-10 border border-warning rounded-lg">
            <Activity size={16} className="text-warning flex-shrink-0" />
            <p className="text-xs text-warning font-medium">
              Reading overdue - Last reading was {days_since} days ago
            </p>
          </div>
        )}

        {/* Faulty Meter Warning */}
        {(meter  as any).status === 'FAULTY' && (
          <div className="flex items-center gap-2 p-3 bg-error bg-opacity-10 border border-error rounded-lg">
            <AlertTriangle size={16} className="text-error flex-shrink-0" />
            <p className="text-xs text-error font-medium">
              Meter requires maintenance or replacement
            </p>
          </div>
        )}

        {/* Actions */}
        {show_actions && (
          <div className="flex flex-col gap-2 pt-3 border-t border-gray-200">
            {/* Primary action: Record Reading (for active meters only) */}
            {(meter as any).status === 'ACTIVE' && on_add_reading && (
              <Button
                variant="primary"
                size="sm"
                full_width
                icon_left={<Activity size={16} />}
                onClick={(e) => {
                  e?.stopPropagation();
                  on_add_reading(meter);
                }}
              >
                Record Reading
              </Button>
            )}

            {/* Secondary actions */}
            <div className="grid grid-cols-2 gap-2">
              {on_view && (
                <Button
                  variant="outline"
                  size="sm"
                  icon_left={<Eye size={16} />}
                  onClick={(e) => {
                    e?.stopPropagation();
                    on_view(meter);
                  }}
                >
                  View
                </Button>
              )}
              {on_edit && (
                <Button
                  variant="outline"
                  size="sm"
                  icon_left={<Edit size={16} />}
                  onClick={(e) => {
                    e?.stopPropagation();
                    on_edit(meter);
                  }}
                >
                  Edit
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

// Grid wrapper component for displaying multiple meter cards
interface MeterCardGridProps {
  meters: Meter[];
  on_view?: (meter: Meter) => void;
  on_edit?: (meter: Meter) => void;
  on_add_reading?: (meter: Meter) => void;
  columns?: 1 | 2 | 3 | 4;
  compact?: boolean;
}

export const MeterCardGrid: React.FC<MeterCardGridProps> = ({
  meters,
  on_view,
  on_edit,
  on_add_reading,
  columns = 3,
  compact = false
}) => {
  const column_classes = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
  };

  if (meters.length === 0) {
    return (
      <div className="text-center py-12">
        <Gauge size={64} className="mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-semibold text-charcoal mb-2">No Meters Found</h3>
        <p className="text-gray-600">Start by adding your first meter to the system.</p>
      </div>
    );
  }

  return (
    <div className={`grid ${column_classes[columns]} gap-4`}>
      {meters.map((meter) => (
        <MeterCard
          key={meter.id}
          meter={meter}
          on_view={on_view}
          on_edit={on_edit}
          on_add_reading={on_add_reading}
          compact={compact}
        />
      ))}
    </div>
  );
};